import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { LinkOutlined as AssignIcon, RouteOutlined as RouteIcon } from '@mui/icons-material';
import type { TruckSchema } from '../../types/transportation.type';

function getTruckPlate(truck: TruckSchema): string | null {
  const plate = truck.data?.en_plate_number?.trim() || truck.data?.ar_plate_number?.trim();
  return plate || null;
}

export interface AssignRouteTruckDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  routeName: string;
  availableTrucks: TruckSchema[];
  truckId: string;
  onTruckIdChange: (truckId: string) => void;
  onSubmit: () => void;
}

export default function AssignRouteTruckDialog({
  open,
  onClose,
  loading,
  routeName,
  availableTrucks,
  truckId,
  onTruckIdChange,
  onSubmit,
}: AssignRouteTruckDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 0, sx: { border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, flexShrink: 0 }}>
            <RouteIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {t('transportation.routes.details.assignTruck')}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={routeName} sx={{ display: 'block', mt: 0.25 }}>
              {routeName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }} noValidate>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <TextField
            fullWidth
            size="small"
            select
            label={t('transportation.routes.details.selectTruck')}
            value={truckId}
            onChange={(e) => onTruckIdChange(e.target.value)}
            disabled={availableTrucks.length === 0}
            helperText={availableTrucks.length === 0 ? t('transportation.routes.details.noAvailableTrucks') : undefined}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          >
            {availableTrucks.length === 0 ? (
              <MenuItem disabled value="">{t('transportation.routes.details.noAvailableTrucks')}</MenuItem>
            ) : (
              availableTrucks.map((truck) => {
                const plate = getTruckPlate(truck);
                const secondary = [plate, `${truck.capacity} kg`].filter(Boolean).join(' · ');
                return (
                  <MenuItem key={truck.id} value={truck.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{truck.name}</Typography>
                      {secondary ? <Typography variant="caption" color="text.secondary" noWrap>{secondary}</Typography> : null}
                    </Box>
                  </MenuItem>
                );
              })
            )}
          </TextField>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('transportation.routes.form.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!truckId || loading || availableTrucks.length === 0}
            startIcon={<AssignIcon />}
            sx={{ textTransform: 'none' }}
          >
            {loading ? t('transportation.routes.details.assigning') : t('transportation.routes.details.assign')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
